import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Sparkles,
  PencilLine,
  AlertCircle,
  ChevronDown,
  Send,
  Lock,
  MessageSquare,
} from "lucide-react";
import {
  fetchClients,
  fetchLLMs,
  fetchDefaultPrompt,
  streamChatResponse,
} from "./services/api";

// ----- Utility function -----
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

// ----- Toast Context & Provider -----
const ToastContext = createContext();

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const toast = (message) => {
    const id = uuidv4();
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-6 right-6 space-y-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="flex items-center space-x-2 bg-gray-800 border border-gray-700 text-white p-4 rounded-lg shadow-lg"
          >
            <AlertCircle size={18} className="text-yellow-400" />
            <div>
              {typeof t.message === "object" ? (
                <>
                  {t.message.title && (
                    <div className="font-semibold">{t.message.title}</div>
                  )}
                  {t.message.description && (
                    <div className="text-sm">{t.message.description}</div>
                  )}
                </>
              ) : (
                <div className="text-sm">{t.message}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const useToast = () => useContext(ToastContext);

// ----- Dropdown Component -----
const Dropdown = ({
  options,
  selectedOption,
  onSelect,
  label,
  displayProperty,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-400 mb-1">
        {label}
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between w-full px-4 py-2 rounded-lg bg-gray-700 text-sm transition-all duration-150 border border-gray-600 hover:bg-gray-600",
          isOpen && "shadow-xl"
        )}
      >
        <span>
          {selectedOption
            ? selectedOption[displayProperty]
            : `Select ${label}`}
        </span>
        <ChevronDown
          size={18}
          className={cn("transition-transform duration-200", isOpen && "rotate-180")}
        />
      </button>
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-gray-800 rounded-lg overflow-hidden shadow-lg">
          <div className="max-h-60 overflow-y-auto py-1">
            {options.map((option) => (
              <div
                key={option.id}
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
                className={cn(
                  "px-4 py-2 cursor-pointer text-sm hover:bg-gray-600",
                  selectedOption?.id === option.id && "bg-gray-600"
                )}
              >
                {option[displayProperty]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


const PromptField = ({
  label,
  placeholder = "Type your message...",
  value,
  onChange,
  onSubmit,
  autoFocus = false,
  sendDisabled = false,
  rows = 3,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !sendDisabled) onSubmit();
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-400 mb-1">
        {label}
      </label>
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={rows}
          className="w-full p-3 pr-12 resize-none rounded bg-gray-800 text-white placeholder-gray-500 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => {
            if (value.trim() && !sendDisabled) onSubmit();
          }}
          disabled={!value.trim() || sendDisabled}
          className={cn(
            "absolute right-2 bottom-2 p-2 rounded-full transition-all duration-200",
            value.trim() && !sendDisabled
              ? "text-blue-400 hover:bg-blue-600 hover:scale-110"
              : "text-gray-500 cursor-not-allowed"
          )}
        >
          <Send size={20} />
        </button>
      </div>
    </div>
  );
};

const ResponseDisplay = ({ messages }) => {
  const containerRef = useRef(null);

  const groups = [];
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].type !== "system") {
      const group = [messages[i]];
      if (i + 1 < messages.length && messages[i + 1].type === "system") {
        group.push(messages[i + 1]);
        i++; 
      }
      groups.push(group);
    } else {
      groups.push([messages[i]]);
    }
  }

  const reversedGroups = [...groups].reverse();
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [messages]);

  const [expandedIndexes, setExpandedIndexes] = useState({});

  const toggleExpand = (id) => {
    setExpandedIndexes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div
      className="flex flex-col space-y-4 overflow-y-auto p-4"
      ref={containerRef}
    >
      {reversedGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center p-8">
          <div className="text-gray-500 mb-2 text-lg font-light">
            No messages yet
          </div>
          <p className="text-sm text-gray-600 max-w-md">
            Select a client and an LLM model, then send a message to start the conversation.
          </p>
        </div>
      ) : (
        reversedGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="space-y-2">
            {group.map((message) => {
              const isSystem = message.type === "system";
              const isExpanded = expandedIndexes[message.id] || false;
              const confidencePercent =
                message.confidence !== undefined
                  ? (message.confidence * 100).toFixed(0) + "%"
                  : null;

              const label = isSystem ? "Assistant" : "You";
              return (
                <div
                  key={message.id}
                  className={cn(
                    "p-4 rounded-lg shadow-md",
                    isSystem ? "bg-gray-800" : "bg-gray-700"
                  )}
                >
                  <div className="flex items-center mb-2">
                    {isSystem ? (
                      <MessageSquare size={16} className="text-blue-300 mr-2" />
                    ) : (
                      <span className="mr-2">💬</span>
                    )}
                    <span className="text-sm font-semibold text-blue-300">
                      {label}
                    </span>
                  </div>
                  <div className="text-sm leading-relaxed">{message.content}</div>
                  {isSystem && (
                    <div className="flex items-center justify-between mt-4 text-xs">
                      <button
                        onClick={() => toggleExpand(message.id)}
                        className="text-gray-300 hover:text-blue-400 transition"
                      >
                        Sources &amp; Context
                      </button>
                      {confidencePercent && (
                        <div
                          className="bg-red-600 text-white px-2 py-0.5 rounded"
                          title={`Confidence: ${confidencePercent}`}
                        >
                          {confidencePercent}
                        </div>
                      )}
                    </div>
                  )}
                  {isSystem && isExpanded && (
                    <div className="mt-3 border-t border-gray-600 pt-2 text-xs text-gray-300 space-y-1">
                      {message.source && (
                        <div>
                          <strong>Source:</strong>{" "}
                          <a
                            href={message.source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline text-blue-400"
                          >
                            {message.source}
                          </a>
                        </div>
                      )}
                      {message.context && (
                        <div>
                          <strong>Context:</strong> {message.context}
                        </div>
                      )}
                      {message.contexts && (
                        <div>
                          <strong>Contexts:</strong> {message.contexts}
                        </div>
                      )}
                      {message.scores && (
                        <div>
                          <strong>Scores:</strong> {message.scores}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};

// ----- Simple Tabs Components -----
const Tabs = ({ activeTab, onTabChange, children }) => {
  return (
    <div>
      <div className="flex space-x-3 mb-4">
        {React.Children.map(children, (child) => {
          if (child.type === TabTrigger) {
            return React.cloneElement(child, { activeTab, onTabChange });
          }
          return null;
        })}
      </div>
      {React.Children.map(children, (child) => {
        if (child.type === TabContent && child.props.value === activeTab) {
          return child;
        }
        return null;
      })}
    </div>
  );
};

const TabTrigger = ({ value, activeTab, onTabChange, children }) => (
  <button
    onClick={() => onTabChange(value)}
    className={cn(
      "flex items-center px-4 py-2 rounded-md font-medium transition-colors duration-200 focus:outline-none",
      activeTab === value
        ? "bg-blue-600 text-white shadow-md"
        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
    )}
  >
    {children}
  </button>
);

const TabContent = ({ value, children }) => {
  return <div>{children}</div>;
};

const ChatInterface = () => {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [llmModels, setLLMModels] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedLLM, setSelectedLLM] = useState(null);
  const [defaultPromptTemplate, setDefaultPromptTemplate] = useState("");
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [activePromptType, setActivePromptType] = useState("custom");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => uuidv4());

  useEffect(() => {
    if (selectedClient) {
      loadClientData(selectedClient);
    }
  }, [selectedClient]);

  const loadInitialData = async () => {
    try {
      const clientsData = await fetchClients();
      setClients(clientsData);
      const llmsData = await fetchLLMs();
      setLLMModels(llmsData);
      if (clientsData.length > 0) setSelectedClient(clientsData[0]);
      if (llmsData.length > 0) setSelectedLLM(llmsData[0]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast("Failed to load initial data. Please refresh the page.");
    }
  };

  useEffect(() => {
    loadInitialData();
  },[])
  

  const loadClientData = async (client) => {
    try {
      const promptTemplate = await fetchDefaultPrompt(client.name);
      setDefaultPromptTemplate(promptTemplate);
      setDefaultPrompt(promptTemplate);
    } catch (error) {
      console.error(`Error loading data for client ${client.name}:`, error);
      toast(`Failed to load data for client ${client.name}.`);
    }
  };

  // Function to reset states when changing client
  const handleClientChange = (client) => {
    setSelectedClient(client);
    setSessionId(uuidv4());
    setMessages([]);
    setCustomPrompt("");
    setDefaultPrompt("");
  };

  const processStreamChunk = useCallback(
    (chunk, done, fullContent, context, confidence, source, contexts, scores) => {
      setMessages((prev) => {
        const lastIndex = prev.length - 1;
        let confidenceValue =
          typeof confidence === "string" ? parseFloat(confidence) : confidence;
        if (lastIndex < 0 || prev[lastIndex].type !== "system") {
          return [
            ...prev,
            {
              id: Date.now().toString(),
              type: "system",
              content: chunk,
              timestamp: new Date(),
              context: context,
              confidence: confidenceValue,
              source: source,
              contexts: contexts,
              scores: scores,
            },
          ];
        } else {
          const updated = [...prev];
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: fullContent || updated[lastIndex].content,
            confidence:
              updated[lastIndex].confidence !== undefined
                ? updated[lastIndex].confidence
                : confidenceValue,
            source: updated[lastIndex].source || source,
          };
          return updated;
        }
      });
    },
    []
  );

  const handleSendMessage = async () => {
    if (!selectedLLM) {
      toast("Please select an LLM model before sending a message.");
      return;
    }
    if (!selectedClient) {
      toast("Please select a client before sending a message.");
      return;
    }
    if (activePromptType === "default") {
      toast("Cannot send from default prompt. Please switch to query.");
      return;
    }
    const userMessage = customPrompt.trim();
    if (!userMessage) {
      toast("Please enter a message to send.");
      return;
    }
    if (!defaultPrompt.trim()) {
      toast("Please provide a default prompt template before sending messages.");
      return;
    }

    const userChatMessage = {
      id: Date.now().toString(),
      type: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userChatMessage]);
    setCustomPrompt("");
    setIsLoading(true);

    try {
      let fullResponseContent = "";
      await streamChatResponse(
        selectedClient,
        selectedLLM,
        defaultPrompt,
        userMessage,
        sessionId,
        (chunk, done, fullContent, context, confidence, source, contexts, scores) => {
          if (fullContent) {
            fullResponseContent = fullContent;
          } else {
            fullResponseContent += chunk;
          }
          processStreamChunk(
            chunk,
            done,
            fullResponseContent,
            context,
            confidence,
            source,
            contexts,
            scores
          );
        }
      );
    } catch (error) {
      console.error("Chat error:", error);
      toast("Failed to get a response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6"> 
        <div className="max-w-6xl mx-auto flex flex-col h-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Dropdown
              options={clients}
              selectedOption={selectedClient}
              onSelect={handleClientChange}
              label="Client"
              displayProperty="name"
            />
            <Dropdown
              options={llmModels}
              selectedOption={selectedLLM}
              onSelect={setSelectedLLM}
              label="LLM Model"
              displayProperty="name"
            />
          </div>
          <Tabs activeTab={activePromptType} onTabChange={setActivePromptType}>
            <TabTrigger value="custom">
              <PencilLine size={16} className="mr-1" />
              Query
            </TabTrigger>
            <TabTrigger value="default">
              <Sparkles size={16} className="mr-1" />
              Default Prompt
            </TabTrigger>
            <TabContent value="default">
              <div className="rounded-lg p-4 mb-4 bg-yellow-500/10 border border-yellow-600/30 flex items-start gap-3">
                <AlertCircle size={18} className="text-yellow-500" />
                <p className="text-xs text-yellow-200">
                  This is the system prompt template that guides the AI. You can edit it, but it will only be used when you send a message from the query tab.
                </p>
              </div>
              <PromptField
                label="Default Prompt Template"
                placeholder="Edit system prompt template..."
                value={defaultPrompt}
                onChange={setDefaultPrompt}
                onSubmit={() =>
                  toast("Switch to query to send messages.")
                }
                sendDisabled={isLoading}
                autoFocus
                rows={20}
              />
            </TabContent>
            <TabContent value="custom">
              {/* Modified layout: input box on top, responses below */}
              <div className="flex flex-col h-[calc(100vh-200px)]">
                <div className="mb-4">
                  <PromptField
                    label="Your Message"
                    placeholder="Type your message here..."
                    value={customPrompt}
                    onChange={setCustomPrompt}
                    onSubmit={handleSendMessage}
                    sendDisabled={isLoading}
                    autoFocus
                  />
                </div>
                <div className="flex-1 overflow-y-auto bg-gray-800 rounded-lg shadow-inner p-4">
                  <ResponseDisplay messages={messages} />
                </div>
              </div>
            </TabContent>
          </Tabs>
        </div>
      )
    </div>
  );
};

// ----- App Wrapper -----
const App = () => {
  return (
    <ToastProvider>
      <ChatInterface />
    </ToastProvider>
  );
};

export default App;

